import _, {isString, noop} from 'lodash';
import React, {FC, ReactNode, useEffect, useMemo, useState} from 'react';
import {Cell, Row, TableState} from 'react-table';
import styled from 'styled-components';

import {Renderer} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {InterfaceEventsEnum} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {CheckBox} from '../../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {
  AnalyticsDataPointTypesEnum,
  AnalyticsTableMetricColumn,
  AnalyticsTableMetricRow,
  AnalyticsTableMetricsEnum,
} from '../../../../models/api/analyticsMetricsModel';
import {setMetricDetailsAction} from '../../../../store/panels/analytics/details/analyticsPanelDetailsActions';
import {
  getAnalyticsSelectedMetrics,
  makeGetAnalyticsMetric,
} from '../../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {getTableIdFilterText, useTableIdFilter} from '../../../helpers/analyticsTableFilters';
import {useAnalyticsUpdateSelectedMetrics} from '../../../helpers/hooks/useAnalyticsUpdateSelectedMetrics';
import {getMetricLabels} from '../../../helpers/metricLabels';
import {
  ContributingSegmentsOptionsEnum,
  ReportElementIdsEnum,
  ReportFilterOptionsEnum,
} from '../../../helpers/reportHelpers/reportTypes';
import {useAnalyticsTracking} from '../../../helpers/tracking/useAnalyticsTracking';
import {ReportGridItem} from '../../reportViewer/reportGrid';
import {Table} from '../../table/table';
import {TableExportViewDialog} from '../../table/tableExportViewDialog';
import {defaultEmptyStateRenderer, isEmptyMetric} from '../charts/emptyState';
import {ReportElementProps, ReportElementWrapperProps} from '../reportElement';
import {AnalyticsEducationTooltip, AnalyticsTooltip} from '../tooltips/analyticsTooltips';
import {TableColumnPicker} from './tableColumnPicker';
import {TableExportSelector} from './tableExportSelector';
import {
  accountColumnId,
  canColumnBeHidden,
  CustomTableColumn,
  isCustomTableColumn,
  mapMetricColumnsToTableColumns,
  mapMetricRowToTableRow,
  TableData,
  teamColumnId,
} from './tableHelpers';

/*
 * Constants.
 */

/**
 * Columns (other than the first column - which is never selectable, unless within knowledge base report)
 * that are not selectable in the tables
 */
const nonSelectableColumnIds = [
  'sla_time_goal',
  'teammate_email',
  teamColumnId,
  accountColumnId,
  'page_view',
  'unique_page_view',

  // All columns for table CHATBOT_PERFORMANCE_TABLE are not selectable
  'chatbot',
  'flows_initiated',
  'flows_completed',
  'chats_deflected',
  'deflection_rate',
  'drop_off_rate',
  'time_to_completion',

  // All columns for table CHATBOT_AI_ANSWERS_TABLE are not selectable
  'ai_participation_rate',
  'ai_reached_count',
  'ai_answer_rate',
  'ai_answer_count',
  'ai_success_rate',
  'ai_resolution_rate',
  'ai_resolution_count',
];

/*
 * Styles.
 */

const FilterChromelessButton = styled(ChromelessButton)`
  display: flex;

  margin-left: 12px;
  font-weight: ${fontWeights.normal};
  font-size: ${fontSizes.medium};
  line-height: 16px;
`;

const StyledCheckBoxLabel = styled.div`
  margin-left: 8px;
`;

const TitleWithTooltipWrapper = styled.div`
  ${flexCenter};
`;

const StyledAnalyticsTooltip = styled(AnalyticsTooltip)`
  margin-left: 8px;
`;

/*
 * Props.
 */

interface ReportTableProps extends ReportElementProps<AnalyticsTableMetricsEnum>, ReportElementWrapperProps {
  defaultColumnIds?: Array<string>;
  blockedColumnIds?: Array<string>;
  disabledColumnIds?: Array<string>;
  disableSelection?: boolean;
  shouldDisableClick?: boolean;
  shouldAllowFirstColumnSelection?: boolean;
  initialState?: Partial<TableState<AnalyticsTableMetricRow>>;
  renderDropdown?: Renderer;
  onClickCustom?: (metric: AnalyticsTableMetricsEnum, cell: Cell<TableData>) => void;
  disableSortBy?: boolean;
  disableGlobalFilter?: boolean;
  disableTablePagination?: boolean;
  displayTableTitleBar?: boolean;
}

/*
 * Component.
 */

export const ReportTable: FC<ReportTableProps> = ({
  isLoading,
  metric,
  reportSectionId,
  defaultColumnIds,
  blockedColumnIds,
  disabledColumnIds,
  disableSelection,
  renderEmptyState = defaultEmptyStateRenderer,
  renderDropdown,
  initialState,
  shouldDisableClick,
  shouldAllowFirstColumnSelection,
  onClickCustom,
  disableSortBy,
  disableGlobalFilter,
  disableTablePagination,
  displayTableTitleBar = true,
  ...props
}) => {
  const dispatch = useAppDispatch();

  const {updateSelectedMetrics} = useAnalyticsUpdateSelectedMetrics();

  const [shownColumnIds, setShownColumnIds] = useState<ReadonlyArray<string>>([]);
  const [isFilteringIds, setIsFilteringIds] = useState(true);
  const [shouldShowExportTableDialog, setShowExportTableDialog] = useState(false);

  const [metricData, metricError] = useAppSelector(makeGetAnalyticsMetric(metric));
  const selectedMetrics = useAppSelector(getAnalyticsSelectedMetrics);
  // Selector and Hook for checkbox filtering.
  const {isRowFilteredPredicate, showRowFilterCheckbox} = useTableIdFilter(metric, isFilteringIds);

  const labels = getMetricLabels(metric);
  const {title = labels.label, infoTooltip = labels.tooltip} = props;

  // don't select disabled columns by default
  const filteredDefaultColumns = useMemo(
    () => defaultColumnIds?.filter((id) => !disabledColumnIds?.includes(id)),
    [defaultColumnIds, disabledColumnIds],
  );

  // This will trigger when we select a view or change the selected metric.
  useEffect(() => {
    if (selectedMetrics[reportSectionId]) {
      setShownColumnIds(selectedMetrics[reportSectionId]);
    } else {
      setShownColumnIds(filteredDefaultColumns || canBeHiddenColumnIds);
    } // reset to original values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMetrics]);

  const trackAnalytics = useAnalyticsTracking();

  const isEmpty = !isLoading && isEmptyMetric(metricData);

  const [fakeColumns, fakeRows] = useMemo(
    () => getMockedData(filteredDefaultColumns),
    [filteredDefaultColumns],
  );

  const [columns, data] = useMemo(() => {
    if (isLoading && (!metricData || metricData.rows?.length === 0)) {
      return [
        mapMetricColumnsToTableColumns(metric, fakeColumns, reportSectionId),
        fakeRows.map((row) => mapMetricRowToTableRow(fakeColumns, row)),
      ];
    }

    if (metricData) {
      const filteredColumns = metricData.columns.filter((column) => !blockedColumnIds?.includes(column.id));

      return [
        // react-table requires the columns to be memoized.
        mapMetricColumnsToTableColumns(metric, filteredColumns, reportSectionId),
        // mapMetricRowToTableRow takes the columns _not_ the filtered columns because the data is not
        // keyed, just an ordered array coming in as the raw rows - so we need the full columns here to
        // properly filter the data and block the column ids that we want to
        metricData.rows.map((row) => mapMetricRowToTableRow(metricData.columns, row, blockedColumnIds)) ?? [],
      ];
    }

    return [[], []];
  }, [isLoading, metricData, fakeColumns, fakeRows, metric, blockedColumnIds]);

  const canBeHiddenColumnIds = useMemo(
    () =>
      columns
        .filter((column) => canColumnBeHidden(column.type))
        .map((column) => column.id)
        .filter(isDefined),
    [columns],
  );

  function isColumnWithIdAndHeader(
    column: CustomTableColumn<TableData>,
  ): column is CustomTableColumn<TableData> & {id: string; Header: string} {
    return isString(column.id) && isDefined(column.Header);
  }

  const columnNames = useMemo(
    () =>
      _(columns)
        .filter(isColumnWithIdAndHeader)
        .keyBy((column) => column.id)
        .mapValues((column) => column.Header)
        .value(),
    [columns],
  );

  const hiddenColumnIds = useMemo(
    () => canBeHiddenColumnIds.filter((columnId) => !shownColumnIds.includes(columnId)),
    [shownColumnIds, canBeHiddenColumnIds],
  );

  const onColumnClickHandler = (columnId: string) => {
    const isCurrentlyShown = shownColumnIds.includes(columnId);
    const newColumnIds = isCurrentlyShown
      ? shownColumnIds.filter((cId) => cId !== columnId)
      : [...shownColumnIds, columnId];

    trackAnalytics(InterfaceEventsEnum.ANALYTICS_COLUMN_LIST_EDIT, {
      report_section: reportSectionId,
      metric_type: columnId,
      selection_type: isCurrentlyShown ? 'remove' : 'add',
    });

    setShownColumnIds(newColumnIds);
    updateSelectedMetrics(newColumnIds, reportSectionId);
  };

  const renderColumnPicker = () => (
    <TableColumnPicker
      isLoading={isLoading}
      canBeHiddenColumnIds={canBeHiddenColumnIds}
      hiddenColumnIds={hiddenColumnIds}
      disabledColumnIds={disabledColumnIds || []}
      columnNames={columnNames}
      onColumnClick={onColumnClickHandler}
    />
  );

  const renderExportSelector = () => (
    <TableExportSelector isLoading={isLoading} clickHandler={() => setShowExportTableDialog(true)} />
  );

  const idFilterLabel = getTableIdFilterText(metric);
  const filteredData = useMemo(() => data.filter(isRowFilteredPredicate), [data, isRowFilteredPredicate]);

  const onCellSelect = (cell: Cell<TableData>) => {
    const {column} = cell;
    if (!isCustomTableColumn(column)) {
      return null;
    }
    if (onClickCustom) {
      return onClickCustom(metric, cell);
    }
    return dispatch(
      setMetricDetailsAction(
        metric,
        {
          [ReportFilterOptionsEnum.COLLECT_CONTRIBUTING_SEGMENT_IDS]: {
            [ContributingSegmentsOptionsEnum.SUB_METRIC]: column.from || column.id,
            [ContributingSegmentsOptionsEnum.ROW]: cell.row.cells[0].value.id,
          },
        },
        column.Header?.toString(),
      ),
    );
  };

  return (
    <>
      <ReportGridItem>
        <Table
          displayTableTitleBar={displayTableTitleBar}
          columns={columns}
          hiddenColumnIds={hiddenColumnIds}
          data={filteredData}
          isLoading={isLoading}
          isEmpty={isEmpty}
          renderEmptyState={renderEmptyState}
          metricError={metricError}
          title={renderTitle(
            title,
            reportSectionId,
            infoTooltip,
            showRowFilterCheckbox,
            idFilterLabel,
            isFilteringIds,
            setIsFilteringIds,
          )}
          renderCustom={renderDropdown}
          renderColumnPicker={renderColumnPicker}
          renderExportSelector={renderExportSelector}
          disableSelection={disableSelection}
          initialState={initialState}
          globalFilterOption={globalFilter}
          isFirstColumnSticky
          onCellSelect={!isLoading && !shouldDisableClick ? onCellSelect : undefined}
          isCellSelectable={(cell) => isCellSelectable(cell, shouldAllowFirstColumnSelection)}
          disableSortBy={disableSortBy}
          disableGlobalFilter={disableGlobalFilter}
          disableTablePagination={disableTablePagination}
        />
      </ReportGridItem>
      {/* Export Dialog */}
      {shouldShowExportTableDialog && (
        <TableExportViewDialog
          onRequestClose={() => setShowExportTableDialog(false)}
          data={filteredData}
          columns={columns}
          title={reportSectionId}
        />
      )}
    </>
  );
};

/*
 * Helpers.
 */

function getMockedData(
  defaultColumnIds: Array<string> | undefined,
): [ReadonlyArray<AnalyticsTableMetricColumn>, ReadonlyArray<AnalyticsTableMetricRow>] {
  const columnIds = defaultColumnIds?.slice(0, 6) || Array.from({length: 6}, () => ''); // If no defaultColumnIds fill with 6 fake columns.
  const fakeColumns: Array<AnalyticsTableMetricColumn> = columnIds.map((columnId, index) => ({
    id: columnId,
    from: columnId,
    label: columnId,
    type: index === 0 ? AnalyticsDataPointTypesEnum.TEXT : AnalyticsDataPointTypesEnum.NUMERICAL,
    sort: undefined,
    tooltip: undefined,
  }));

  const fakeRows: Array<AnalyticsTableMetricRow> = Array.from({length: 10}, () =>
    Array.from(fakeColumns, (column) => {
      if (column.type === AnalyticsDataPointTypesEnum.TEXT) {
        return {
          type: column.type,
          value: 'loading',
          previousValue: 'loading',
        };
      }

      return {
        type: AnalyticsDataPointTypesEnum.NUMERICAL,
        value: 0,
        previousValue: 0,
      };
    }),
  );

  return [fakeColumns, fakeRows];
}

function renderTitle(
  title: ReactNode | undefined,
  reportSectionId: ReportElementIdsEnum,
  infoTooltip: AnalyticsEducationTooltip | undefined,
  showFilterCheckbox: boolean,
  idFilterText: ReactNode,
  isFilteringIds: boolean,
  setIsFilteringIds: (val: boolean) => void,
) {
  if (!infoTooltip) {
    return title;
  }

  return (
    <TitleWithTooltipWrapper>
      <div>{title}</div>
      <StyledAnalyticsTooltip tooltip={infoTooltip} reportSectionId={reportSectionId} />
      {showFilterCheckbox && idFilterText && (
        <FilterChromelessButton onClick={() => setIsFilteringIds(!isFilteringIds)}>
          <CheckBox isChecked={isFilteringIds} onChange={noop} size={VisualSizesEnum.SMALL} />
          <StyledCheckBoxLabel>{idFilterText}</StyledCheckBoxLabel>
        </FilterChromelessButton>
      )}
    </TitleWithTooltipWrapper>
  );
}

function isCellSelectable(cell: Cell<TableData>, shouldAllowFirstColumnSelection?: boolean) {
  if (cell.column.index === 0 && shouldAllowFirstColumnSelection) {
    return true;
  }
  // First columns aren't selectable, as well as SLA column showing config.
  // @TODO Support this in a more generic way. And cells without data shouldn't be clickable either.
  return cell.column.index !== 0 && !nonSelectableColumnIds.includes(cell.column.id);
}

// Global filtering method.
function globalFilter(rows: Array<Row<TableData>>, columnIds: Array<string>, globalFilterValue: string) {
  return rows.filter((row) =>
    columnIds.some((columndId) =>
      row.values[columndId]?.value?.toString().toLowerCase().includes(globalFilterValue.toLowerCase()),
    ),
  );
}
